import { FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Modal from 'components/layouts/modal';
import useApi from 'utils/use-api';
import wrapClick from 'utils/wrap-click';
import AssigneeSelector from 'containers/assignee-selector';

interface ReassignModalProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  requestId: number;
  onSuccess: () => void;
}

const validationSchema = Yup.object({
  assigneeType: Yup.string().oneOf(['group', 'user'], 'Invalid assignee type').required('Assignee type is required'),
  assigneeId: Yup.number().required('Assignee is required'),
  assigneeName: Yup.string().required('Assignee name is required'),
});

const ReassignModal: FC<ReassignModalProps> = ({ open, setOpen, requestId, onSuccess }) => {
  const api = useApi();

  const form = useFormik({
    initialValues: {
      assigneeType: 'group' as 'group' | 'user',
      assigneeId: 0,
      assigneeName: '',
    },
    validationSchema,
    onSubmit: async values => {
      await reassignMutation.mutateAsync(values);
    },
  });

  const reassignMutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      const response = await api.post(`/access-requests/${requestId}/reassign`, {
        assigneeType: values.assigneeType,
        assigneeId: Number(values.assigneeId),
        assigneeName: values.assigneeName,
      });
      return response.data;
    },
    onSuccess: () => {
      form.resetForm();
      onSuccess();
    },
  });

  const renderActions = () => {
    return (
      <button
        type="button"
        onClick={wrapClick(() => form.handleSubmit())}
        disabled={reassignMutation.isPending}
        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {reassignMutation.isPending ? 'Reassigning...' : 'Reassign Request'}
      </button>
    );
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Reassign Request"
      description="Choose a new assignee for this request"
      size="4xl"
      renderActions={renderActions}
    >
      <div className="space-y-6 mt-4">
        <AssigneeSelector
          form={form}
          groupHelpText="Select a group to assign this request to. All members will be notified."
          userHelpText="Select a user to assign this request to."
        />
      </div>
    </Modal>
  );
};

export default ReassignModal;
